/**
 * Training in den Kalender (F-34 E1) - rein und getestet.
 *
 * - Jede Einheit des aktiven Plans wird an ihrem Tag ein eigener Termin, mit
 *   ihrer Uhrzeit (sonst der Standard-Uhrzeit) und ihrer Dauer (von Hand oder
 *   geschaetzt). Je Tag gilt die Planversion dieses Tages.
 * - Uhrzeiten sind Wanduhrzeiten („floating“): 7:00 bleibt 7:00, auch ueber
 *   die Zeitumstellung hinweg.
 * - Konflikt = Ueberschneidung mit einem Termin mit Uhrzeit (ganztaegige
 *   wie Ferien zaehlen nicht).
 * - Freie Zeit = die Luecke am selben Tag im Zeitfenster (Standard 6-22 Uhr),
 *   die lang genug ist und der geplanten Uhrzeit am naechsten liegt.
 * - Schon eingetragen (gleiche Einheit am gleichen Plantag) wird nicht doppelt angelegt.
 */
import { dauerSchaetzen, einheitenAm, planAm, versionAm } from '@/domain/training/plan';
import type { PlanEinheit, TrainingsPlan, Uebung } from '@/domain/training/typen';
import type { ID, LocalDate, LocalDateTime, TrainingsTermin } from '@/domain/types';
import { addDays, atMinutes, datePart, minutesOfDay } from '@/lib/datetime';

export const STANDARD_UHRZEIT = '17:00';
export const STANDARD_FENSTER = { von: '06:00', bis: '22:00' };

export interface KalenderVorschlag {
  key: string;
  datum: LocalDate;
  einheit: PlanEinheit;
  plan: TrainingsPlan;
  start: LocalDateTime;
  end: LocalDateTime;
  dauerMin: number;
  /** Dauer geschaetzt (nicht von Hand gesetzt). */
  geschaetzt: boolean;
}

export interface Belegt { start: LocalDateTime; end: LocalDateTime; titel: string }

const minuten = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return (h ?? 0) * 60 + (m ?? 0); };

/** Termin am Tag ab Minute `ab`, hoechstens bis 23:59 (floating, ohne Zeitumstellung). */
export function zeitraumAm(datum: LocalDate, ab: number, dauerMin: number): { start: LocalDateTime; end: LocalDateTime } {
  return { start: atMinutes(datum, ab), end: atMinutes(datum, Math.min(ab + dauerMin, 24 * 60 - 1)) };
}

export function vorschlaege(
  plaene: TrainingsPlan[], einheiten: PlanEinheit[], bibliothek: ReadonlyMap<string, Uebung>,
  von: LocalDate, bis: LocalDate, standardUhrzeit = STANDARD_UHRZEIT,
): KalenderVorschlag[] {
  const out: KalenderVorschlag[] = [];
  for (let d = von; d <= bis; d = addDays(d, 1)) {
    const plan = planAm(plaene, d);
    if (!plan) continue;
    for (const e of einheitenAm(plan, einheiten, d)) {
      const s = dauerSchaetzen(e, bibliothek, plan);
      const dauerMin = Math.max(5, Math.round(s.minuten));
      out.push({ key: `${e.id}|${d}`, datum: d, einheit: e, plan, ...zeitraumAm(d, minuten(e.uhrzeit ?? standardUhrzeit), dauerMin), dauerMin, geschaetzt: !s.manuell });
    }
  }
  return out;
}

const ueberschneiden = (a: { start: string; end: string }, b: { start: string; end: string }) => a.start < b.end && b.start < a.end;

export function konflikte(v: Pick<KalenderVorschlag, 'start' | 'end'>, belegt: Belegt[]): Belegt[] {
  return belegt.filter((b) => ueberschneiden(v, b));
}

/**
 * Freie Zeit am Tag im Fenster, in die `dauerMin` passt - so nah wie moeglich
 * an der Wunschzeit (Standard: Fensterbeginn); `null`, wenn nichts passt.
 */
export function freieZeit(datum: LocalDate, dauerMin: number, belegt: Belegt[], fenster = STANDARD_FENSTER, wunsch?: LocalDateTime): LocalDateTime | null {
  const anfang = minuten(fenster.von);
  const ende = minuten(fenster.bis);
  const ziel = wunsch ? minutesOfDay(wunsch) : anfang;
  const amTag = belegt
    .filter((b) => datePart(b.start) <= datum && datePart(b.end) >= datum)
    .map((b) => ({ von: datePart(b.start) < datum ? 0 : minutesOfDay(b.start), bis: datePart(b.end) > datum ? 24 * 60 : minutesOfDay(b.end) }))
    .sort((a, b) => a.von - b.von);
  // Luecken im Fenster sammeln, dann je Luecke den Start, der der Wunschzeit am naechsten liegt.
  const luecken: [number, number][] = [];
  let t = anfang;
  for (const b of amTag) {
    if (b.von > t) luecken.push([t, Math.min(b.von, ende)]);
    t = Math.max(t, b.bis);
  }
  if (t < ende) luecken.push([t, ende]);
  let best: number | null = null;
  for (const [a, e] of luecken) {
    if (e - a < dauerMin) continue;
    const s = Math.min(Math.max(ziel, a), e - dauerMin);
    if (best === null || Math.abs(s - ziel) < Math.abs(best - ziel)) best = s;
  }
  return best === null ? null : atMinutes(datum, best);
}

export type Entscheidung = { art: 'eintragen' } | { art: 'verschieben'; start: LocalDateTime } | { art: 'weglassen' };

/** Vorschlag fuer die Vorschau und fuers automatische Weiterfuehren: ohne Konflikt eintragen, sonst verschieben, sonst weglassen. */
export function vorschlagEntscheidung(v: KalenderVorschlag, belegt: Belegt[], fenster = STANDARD_FENSTER): Entscheidung {
  if (!konflikte(v, belegt).length) return { art: 'eintragen' };
  const frei = freieZeit(v.datum, v.dauerMin, belegt, fenster, v.start);
  return frei ? { art: 'verschieben', start: frei } : { art: 'weglassen' };
}

/** Start und Ende nach der Entscheidung, `null` bei „weglassen“. */
export function zeitNach(v: KalenderVorschlag, e: Entscheidung): { start: LocalDateTime; end: LocalDateTime } | null {
  if (e.art === 'weglassen') return null;
  if (e.art === 'eintragen') return { start: v.start, end: v.end };
  return zeitraumAm(v.datum, minutesOfDay(e.start), v.dauerMin);
}

/** Ist diese Einheit an diesem Plantag schon im Kalender? */
export const schonEingetragen = (v: Pick<KalenderVorschlag, 'einheit' | 'datum'>, bestehend: Pick<TrainingsTermin, 'planEinheitId' | 'datum'>[]) =>
  bestehend.some((t) => t.planEinheitId === v.einheit.id && t.datum === v.datum);

/**
 * Kuenftige Trainingstermine aus einer Planversion, die an ihrem Tag nicht
 * mehr gilt (Plan wurde geaendert) - Kandidaten fuer „aktualisieren“.
 */
export function veraltet<T extends { training?: TrainingsTermin }>(termine: T[], plaene: TrainingsPlan[], heute: LocalDate): T[] {
  const familie = new Map<ID, ID>(plaene.map((p) => [p.id, p.familie]));
  return termine.filter((t) => {
    if (!t.training || t.training.datum < heute) return false;
    const f = familie.get(t.training.planId);
    const gilt = f ? versionAm(plaene, f, t.training.datum) : null;
    return !gilt || gilt.id !== t.training.planId;
  });
}

export interface PlanZeile {
  v: KalenderVorschlag;
  /** `schon` = bereits im Kalender, wird nicht doppelt angelegt. */
  zustand: 'schon' | 'frei' | 'konflikt';
  konflikte: Belegt[];
  /** Vorschlag fuer die Entscheidung (bei `schon` immer „weglassen“). */
  entscheidung: Entscheidung;
}

/**
 * Die Vorschau: der Reihe nach entscheiden, was schon eingeplant ist, zaehlt
 * als belegt - so ueberschneiden sich auch zwei neue Einheiten nicht.
 */
export function planen(
  liste: KalenderVorschlag[], belegt: Belegt[], bestehend: Pick<TrainingsTermin, 'planEinheitId' | 'datum'>[], fenster = STANDARD_FENSTER,
): PlanZeile[] {
  const belegtNeu = [...belegt];
  return liste.map((v) => {
    if (schonEingetragen(v, bestehend)) return { v, zustand: 'schon', konflikte: [], entscheidung: { art: 'weglassen' } };
    const k = konflikte(v, belegtNeu);
    const entscheidung = vorschlagEntscheidung(v, belegtNeu, fenster);
    const zeit = zeitNach(v, entscheidung);
    if (zeit) belegtNeu.push({ ...zeit, titel: v.einheit.name });
    return { v, zustand: k.length ? 'konflikt' : 'frei', konflikte: k, entscheidung };
  });
}
